import { useState, useRef, useCallback, useEffect } from 'react'
import {
  requestInterviewDisplayMedia,
  validateInterviewScreenShareAsync,
  stopMediaStream,
  interviewShareIssueMessage,
  isScreenShareCancelledError,
  InterviewScreenCaptureError,
  isInterviewScreenCaptureError,
  hasAudibleAudioSignal,
} from '../utils/interviewScreenShare'

export interface UseMediaRecorderOptions {
  onDataAvailable?: (blob: Blob) => void
  onStop?: (blob: Blob) => void
  onError?: (error: Error) => void
  /**
   * Called when recording auto-starts in the background after a
   * 'silent_system_audio' wait resolves (the candidate started talking) —
   * lets the caller close any "waiting for audio" UI it opened in response
   * to the earlier onError, without the caller having to call
   * startRecording() again itself.
   */
  onRecovered?: () => void
  /**
   * Called with `true` right before the native browser share-picker opens,
   * and `false` once it closes (Share/Cancel/error) — lets the caller hide
   * its own "waiting" modal while the browser's own dialog is on screen, so
   * the user isn't looking at two overlapping dialogs at once.
   */
  onPickerOpenChange?: (isOpen: boolean) => void
  mimeType?: string
  /**
   * MediaRecorder's `timeslice` (ms) — how often `ondataavailable` fires
   * with the next incremental chunk. Defaults to 1000ms (the original
   * AI-interview recruiter-recording behavior, which only ever reads
   * `recordedBlob`/`onStop`'s combined blob and doesn't care about the
   * cadence). The proctored-interview candidate flow overrides this with
   * the server-configured recording-chunk interval and actually uploads
   * each chunk via `onDataAvailable` as it arrives, instead of buffering
   * the whole recording client-side for one upload at the end.
   */
  timesliceMs?: number
  /**
   * Whether to gate startRecording() on the shared *system* audio track
   * actually carrying sound before recording begins (waiting/retrying in
   * the background otherwise via watchForCandidateAudio()). Defaults to
   * true — the original AI-interview use case has the recruiter share
   * their screen while on a live call, so "system audio" is the call
   * platform's own output and genuinely needs this check (silence there
   * means the recording isn't capturing the candidate at all).
   *
   * The proctored-interview candidate flow reuses this same hook to record
   * the candidate's OWN screen, alone — there is no call, so the
   * candidate's voice never reaches system audio output (it only reaches
   * micStream, already verified earlier in startRecording()). Requiring
   * system audio there would wait forever, so that caller passes `false`.
   */
  requireAudibleSystemAudio?: boolean
}

export interface UseMediaRecorderReturn {
  isRecording: boolean
  isPaused: boolean
  recordingTime: number
  /** Live level (0-100) of the recruiter's own microphone, sampled independently of systemAudioLevel. */
  micAudioLevel: number
  /** Live level (0-100) of the shared system/call audio (the candidate's side), sampled independently of micAudioLevel. */
  systemAudioLevel: number
  recordedBlob: Blob | null
  startRecording: () => Promise<void>
  stopRecording: () => void
  pauseRecording: () => void
  resumeRecording: () => void
  resetRecording: () => void
  error: Error | null
  isSupported: boolean
}

const isMediaRecorderSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'MediaRecorder' in window &&
  'getUserMedia' in navigator.mediaDevices &&
  typeof navigator.mediaDevices?.getDisplayMedia === 'function'

const getSupportedMimeType = (): string => {
  const types = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/mpeg',
  ]

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }

  return ''
}

export default function useMediaRecorder(
  options: UseMediaRecorderOptions = {}
): UseMediaRecorderReturn {
  const {
    onDataAvailable,
    onStop,
    onError,
    onRecovered,
    onPickerOpenChange,
    mimeType = getSupportedMimeType(),
    timesliceMs = 1000,
    requireAudibleSystemAudio = true,
  } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [micAudioLevel, setMicAudioLevel] = useState(0)
  const [systemAudioLevel, setSystemAudioLevel] = useState(0)
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)
  const systemStreamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  // Separate analysers on the pre-mix mic/system sources (not the combined
  // recorded track) so either side dropping out live is visible on its own —
  // a single combined meter can't tell "you" from "candidate" going silent.
  const micAnalyserRef = useRef<AnalyserNode | null>(null)
  const systemAnalyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  // Bumped on every startRecording() call and on unmount. A background
  // "waiting for candidate audio" loop captures its own value at start and
  // checks it hasn't changed before each retry — lets a fresh manual
  // attempt (or unmount) cancel a stale watch instead of racing it.
  const recoveryGenerationRef = useRef(0)
  // The mic/system streams a background watch is currently holding onto
  // (kept alive, not stopped, while waiting) so a superseding call can find
  // and release them instead of leaking the mic/screen-share indicator.
  const pendingRecoveryStreamsRef = useRef<{ mic: MediaStream; system: MediaStream } | null>(null)

  const releasePendingRecoveryStreams = useCallback(() => {
    if (pendingRecoveryStreamsRef.current) {
      stopMediaStream(pendingRecoveryStreamsRef.current.mic)
      stopMediaStream(pendingRecoveryStreamsRef.current.system)
      pendingRecoveryStreamsRef.current = null
    }
  }, [])

  const cleanup = useCallback(() => {
    recoveryGenerationRef.current += 1
    releasePendingRecoveryStreams()

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    mediaRecorderRef.current = null

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop())
      micStreamRef.current = null
    }

    if (systemStreamRef.current) {
      systemStreamRef.current.getTracks().forEach((track) => track.stop())
      systemStreamRef.current = null
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
    }
    audioContextRef.current = null
    micAnalyserRef.current = null
    systemAnalyserRef.current = null
  }, [])

  useEffect(() => () => cleanup(), [cleanup])

  // Reads both pre-mix analysers on one shared animation-frame loop and
  // updates each side's level independently. Runs unconditionally until
  // externally cancelled (cleanup()/mediaRecorder.onstop both already do
  // this) — it must NOT gate itself on the `isRecording` state: this is
  // called synchronously right after setIsRecording(true), and since React
  // state updates aren't visible until the next render, a self-check here
  // would still see the stale pre-update value and exit after one tick.
  const startDualAudioLevelMonitoring = useCallback(
    (micAnalyser: AnalyserNode | null, systemAnalyser: AnalyserNode | null) => {
      try {
        const micData = micAnalyser ? new Uint8Array(micAnalyser.frequencyBinCount) : null
        const systemData = systemAnalyser ? new Uint8Array(systemAnalyser.frequencyBinCount) : null

        const levelFrom = (analyser: AnalyserNode, data: Uint8Array<ArrayBuffer>) => {
          analyser.getByteFrequencyData(data)
          const average = data.reduce((a, b) => a + b, 0) / data.length
          return Math.min(100, (average / 128) * 100)
        }

        const updateLevels = () => {
          if (micAnalyser && micData) setMicAudioLevel(levelFrom(micAnalyser, micData))
          if (systemAnalyser && systemData) setSystemAudioLevel(levelFrom(systemAnalyser, systemData))

          animationFrameRef.current = requestAnimationFrame(updateLevels)
        }

        updateLevels()
      } catch (err) {
        console.warn('Audio level monitoring not supported:', err)
      }
    },
    []
  )

  // Mixes the mic + system streams, wires up the MediaRecorder, and starts
  // recording. Shared by the normal path and by watchForCandidateAudio()'s
  // background recovery path, which reaches this with the same
  // already-granted streams once audio finally appears.
  const beginRecording = useCallback(
    (micStream: MediaStream, systemStream: MediaStream) => {
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext
      const destination = audioContext.createMediaStreamDestination()

      const micSource = audioContext.createMediaStreamSource(micStream)
      micSource.connect(destination)
      const micAnalyser = audioContext.createAnalyser()
      micAnalyser.fftSize = 256
      micSource.connect(micAnalyser)
      micAnalyserRef.current = micAnalyser

      if (systemStream.getAudioTracks().length > 0) {
        const systemSource = audioContext.createMediaStreamSource(systemStream)
        systemSource.connect(destination)
        const systemAnalyser = audioContext.createAnalyser()
        systemAnalyser.fftSize = 256
        systemSource.connect(systemAnalyser)
        systemAnalyserRef.current = systemAnalyser
      }

      const combinedStream = new MediaStream()

      const videoTrack = systemStream.getVideoTracks()[0]
      if (videoTrack) {
        combinedStream.addTrack(videoTrack)
      }

      const mixedAudioTrack = destination.stream.getAudioTracks()[0]
      if (mixedAudioTrack) {
        combinedStream.addTrack(mixedAudioTrack)
      }

      streamRef.current = combinedStream
      micStreamRef.current = micStream
      systemStreamRef.current = systemStream

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 2500000,
      })

      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
          onDataAvailable?.(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' })
        setRecordedBlob(blob)
        onStop?.(blob)

        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }
        setMicAudioLevel(0)
        setSystemAudioLevel(0)
      }

      mediaRecorder.onerror = () => {
        const err = new Error('MediaRecorder error')
        setError(err)
        onError?.(err)
      }

      mediaRecorder.start(timesliceMs)
      setIsRecording(true)
      setIsPaused(false)
      setRecordingTime(0)

      const startTime = Date.now()
      timerRef.current = window.setInterval(() => {
        if (!isPaused) {
          setRecordingTime(Math.floor((Date.now() - startTime) / 1000))
        }
      }, 1000)

      startDualAudioLevelMonitoring(micAnalyserRef.current, systemAnalyserRef.current)
    },
    [mimeType, timesliceMs, onDataAvailable, onStop, onError, isPaused, startDualAudioLevelMonitoring]
  )

  // Keeps the already-granted mic/system streams alive and keeps sampling
  // system audio in the background — in short waits, indefinitely — until
  // either real audio appears (starts recording automatically and notifies
  // the caller via onRecovered so it can dismiss its "waiting" UI) or this
  // attempt is superseded/cancelled (a fresh startRecording() call, or
  // unmount, bumps recoveryGenerationRef).
  const watchForCandidateAudio = useCallback(
    async (generation: number, micStream: MediaStream, systemStream: MediaStream) => {
      pendingRecoveryStreamsRef.current = { mic: micStream, system: systemStream }

      while (recoveryGenerationRef.current === generation) {
        const audible = await hasAudibleAudioSignal(systemStream, { timeoutMs: 6000 })
        if (recoveryGenerationRef.current !== generation) break
        if (audible) {
          pendingRecoveryStreamsRef.current = null
          beginRecording(micStream, systemStream)
          onRecovered?.()
          return
        }
      }

      // Superseded/cancelled while waiting — release what we were holding,
      // unless a newer call already claimed/cleared this ref first.
      if (pendingRecoveryStreamsRef.current?.mic === micStream) {
        pendingRecoveryStreamsRef.current = null
        stopMediaStream(micStream)
        stopMediaStream(systemStream)
      }
    },
    [beginRecording, onRecovered]
  )

  const startRecording = useCallback(async () => {
    recoveryGenerationRef.current += 1
    const myGeneration = recoveryGenerationRef.current
    releasePendingRecoveryStreams()

    let micStream: MediaStream | null = null
    let systemStream: MediaStream | null = null

    try {
      setError(null)
      setRecordedBlob(null)
      chunksRef.current = []

      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      })
      micStreamRef.current = micStream

      onPickerOpenChange?.(true)
      try {
        systemStream = await requestInterviewDisplayMedia()
      } catch (err) {
        stopMediaStream(micStream)
        micStreamRef.current = null
        micStream = null
        if (isScreenShareCancelledError(err)) {
          throw new InterviewScreenCaptureError(
            'Sharing was cancelled. Choose Entire screen, enable Also share system audio, then Share.',
            'cancel'
          )
        }
        throw new InterviewScreenCaptureError(
          err instanceof Error ? err.message : 'Could not start screen capture.',
          'generic'
        )
      } finally {
        onPickerOpenChange?.(false)
      }

      const validation = await validateInterviewScreenShareAsync(systemStream)
      if (!validation.ok) {
        stopMediaStream(systemStream)
        stopMediaStream(micStream)
        systemStreamRef.current = null
        micStreamRef.current = null
        systemStream = null
        micStream = null
        throw new InterviewScreenCaptureError(
          interviewShareIssueMessage(validation.issue),
          validation.issue ?? 'not_entire_screen'
        )
      }
      systemStreamRef.current = systemStream

      // The share is structurally correct (right surface, checkbox on) —
      // now wait for genuine sound rather than judging on a single instant.
      // If the candidate hasn't spoken within the window, keep both streams
      // alive and keep listening in the background instead of forcing a
      // full re-share; recording begins automatically the moment audio
      // appears, with no further action needed from the recruiter.
      //
      // Only applies when system audio is actually expected to carry the
      // candidate's voice (the recruiter-recording-a-live-call case) — see
      // requireAudibleSystemAudio's doc comment for why the proctored
      // interview's self-recording candidate flow skips this entirely.
      if (requireAudibleSystemAudio) {
        const audible = await hasAudibleAudioSignal(systemStream, { timeoutMs: 6000 })
        if (!audible) {
          void watchForCandidateAudio(myGeneration, micStream, systemStream)
          throw new InterviewScreenCaptureError(
            interviewShareIssueMessage('silent_system_audio'),
            'silent_system_audio'
          )
        }
      }

      beginRecording(micStream, systemStream)
    } catch (err) {
      // watchForCandidateAudio() already owns and is holding these streams
      // in that case — releasing/cleaning them up here would kill the
      // background wait it just started.
      const isWaitingForCandidateAudio =
        isInterviewScreenCaptureError(err) && err.reason === 'silent_system_audio'
      if (!isWaitingForCandidateAudio) {
        stopMediaStream(systemStream)
        stopMediaStream(micStream)
        cleanup()
      }
      if (isInterviewScreenCaptureError(err)) {
        setError(err)
        onError?.(err)
        throw err
      }
      const wrapped = new InterviewScreenCaptureError(
        err instanceof Error ? err.message : 'Failed to start recording',
        'generic'
      )
      setError(wrapped)
      onError?.(wrapped)
      throw wrapped
    }
  }, [
    onError,
    cleanup,
    watchForCandidateAudio,
    beginRecording,
    releasePendingRecoveryStreams,
    requireAudibleSystemAudio,
  ])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
    setIsPaused(false)

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop())
    }

    if (systemStreamRef.current) {
      systemStreamRef.current.getTracks().forEach((track) => track.stop())
    }
  }, [])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause()
      setIsPaused(true)
    }
  }, [])

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume()
      setIsPaused(false)
    }
  }, [])

  const resetRecording = useCallback(() => {
    cleanup()
    setIsRecording(false)
    setIsPaused(false)
    setRecordingTime(0)
    setMicAudioLevel(0)
    setSystemAudioLevel(0)
    setRecordedBlob(null)
    setError(null)
    chunksRef.current = []
  }, [cleanup])

  return {
    isRecording,
    isPaused,
    recordingTime,
    micAudioLevel,
    systemAudioLevel,
    recordedBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    error,
    isSupported: isMediaRecorderSupported(),
  }
}

export function formatRecordingTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
